import { Form } from "react-router-dom";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import AddTeamMutation from "../season/mutations/AddTeam";
import { toast } from "react-toastify";
import { GlobalStyles } from "../../constants/styles";
import { gql, useMutation } from "@apollo/client";
import FilePicker from "../common/FilePicker";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

const UPDATE_TEAM_LOGO = gql`
    mutation UpdateTeamLogo($teamId: ID!, $file: Upload!, $seasonId: ID) {
        updateTeamLogo(teamId: $teamId, file: $file, seasonId: $seasonId) {
            success
            errors
            team {
                id
                logoUrl
            }
        }
    }
`;

export default function AddTeamModal({ teams, setTeams, activeSeasonData, modalElem }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    const [updateTeamLogo] = useMutation(UPDATE_TEAM_LOGO);

    const [teamName, setTeamName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [addTeam] = AddTeamMutation();
    const teamNameInput = useRef(null);

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 800,
                outline: "none",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const refreshTeams = ({ createdOrg, logoUrl }) => {
        if (!createdOrg) return;

        toast.success(t("addTeamModal.teamAddedSuccess"), {
            autoClose: 3000,
            toastId: "team_added",
            position: "top-center",
            closeOnClick: true,
        });

        setTeams((prev) => [
            ...prev,
            {
                teamId: createdOrg.id,
                teamName: createdOrg.name,
                team: {
                    id: createdOrg.id,
                    slug: createdOrg.slug,
                    logoUrl: logoUrl ?? createdOrg.logoUrl ?? null,
                    players: [],
                },
            },
        ]);

        setTeamName("");
        if (teamNameInput.current) teamNameInput.current.value = "";

        closeAddTeamModal();
    };

    const closeAddTeamModal = () => {
        const el = document.getElementById("add-team-modal");
        if (!el) return;

        if (window.$ && typeof window.$(el).modal === "function") {
            window.$(el).modal("hide");
            return;
        }

        const BS = window.bootstrap;
        const Modal = BS?.Modal;

        if (Modal) {
            try {
                const instance =
                    (typeof Modal.getInstance === "function" && Modal.getInstance(el)) ||
                    (typeof Modal.getOrCreateInstance === "function" && Modal.getOrCreateInstance(el)) ||
                    null;

                if (instance && typeof instance.hide === "function") {
                    instance.hide();
                    return;
                }

                const inst = new Modal(el);
                if (inst && typeof inst.hide === "function") {
                    inst.hide();
                    return;
                }
            } catch (e) {
            }
        }

        el.classList.remove("show");
        el.style.display = "none";
        el.setAttribute("aria-hidden", "true");

        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    };

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const clearLogo = useCallback(() => {
        setLogoFile(null);
        setLogoPreviewUrl(null);
    }, []);

    return (
        <>
            <div className="modal fade" id="add-team-modal" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form
                            method="post"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                if (!activeSeasonData?.id) return;

                                if (!teamName?.trim()) {
                                    toast.error(t("addTeamModal.teamNameRequired"), {
                                        autoClose: 2500,
                                        toastId: "team_name_required",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });
                                    return;
                                }

                                try {
                                    setIsSubmitting(true);

                                    const result = await addTeam({
                                        variables: {
                                            name: teamName.trim(),
                                            seasonId: activeSeasonData.id,
                                        },
                                    });

                                    const createdOrg = result?.data?.createTeam?.organization;
                                    const createdTeamId = createdOrg?.id;

                                    let finalLogoUrl = createdOrg?.logoUrl ?? null;

                                    if (logoFile && createdTeamId) {
                                        try {
                                            const up = await updateTeamLogo({
                                                variables: {
                                                    teamId: createdTeamId,
                                                    file: logoFile,
                                                    seasonId: activeSeasonData.id,
                                                },
                                            });

                                            const payload = up?.data?.updateTeamLogo;

                                            if (!payload?.success) {
                                                toast.warn(
                                                    t("addTeamModal.teamCreatedLogoFailed", {
                                                        errors: (payload?.errors || []).join(", "),
                                                    }),
                                                    {
                                                        autoClose: 4500,
                                                        toastId: "team_logo_failed",
                                                        position: "top-center",
                                                    }
                                                );
                                            } else {
                                                finalLogoUrl = payload?.team?.logoUrl ?? finalLogoUrl;
                                                clearLogo();
                                            }
                                        } catch (e) {
                                            toast.warn(
                                                t("addTeamModal.teamCreatedLogoFailed", {
                                                    errors: e.message,
                                                }),
                                                {
                                                    autoClose: 4500,
                                                    toastId: "team_logo_failed",
                                                    position: "top-center",
                                                }
                                            );
                                        }
                                    }

                                    refreshTeams({ createdOrg, logoUrl: finalLogoUrl });
                                } catch (err) {
                                    toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                                        autoClose: 3000,
                                        toastId: "team_add_error",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}
                        >
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("addTeamModal.badge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("addTeamModal.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    aria-label={t("addTeamModal.closeAria")}
                                    style={modalStyles.closeBtn}
                                    onClick={closeAddTeamModal}
                                    disabled={isSubmitting}
                                    title={t("addTeamModal.closeTitle")}
                                >
                                    <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addTeamModal.sectionTitle")}</div>

                                    <FilePicker
                                        scopeId="add-team-modal"
                                        colors={GlobalStyles.colors}
                                        label={t("addTeamModal.logoLabel")}
                                        hint={t("addTeamModal.logoHint")}
                                        accept="image/*"
                                        value={logoFile}
                                        previewUrl={logoPreviewUrl}
                                        onChangeFile={(file) => setLogoFile(file)}
                                        onClear={clearLogo}
                                    />

                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={modalStyles.label}>{t("addTeamModal.teamNameLabel")}</label>
                                        <input
                                            className="form-control"
                                            required
                                            ref={teamNameInput}
                                            type="text"
                                            name="team_name"
                                            placeholder={t("addTeamModal.teamNamePlaceholder")}
                                            style={modalStyles.input}
                                            value={teamName}
                                            onChange={(event) => setTeamName(event.target.value)}
                                            disabled={isSubmitting}
                                            autoFocus
                                        />
                                        <div style={modalStyles.hint}>
                                            {t("addTeamModal.teamNameHint")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={closeAddTeamModal}
                                    style={{ ...modalStyles.btn, ...modalStyles.btnGhost }}
                                    disabled={isSubmitting}
                                >
                                    <i className="fas fa-times" />
                                    {t("addTeamModal.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{ ...modalStyles.btn, ...modalStyles.btnPrimary }}
                                    disabled={isSubmitting || !activeSeasonData?.id}
                                    title={
                                        !activeSeasonData?.id
                                            ? t("addTeamModal.noActiveSeasonSelected")
                                            : t("addTeamModal.addTeamTitle")
                                    }
                                >
                                    <i className="fas fa-check" />
                                    {isSubmitting ? t("addTeamModal.saving") : t("addTeamModal.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
  #add-org-modal,
  #edit-org-modal,
  #add-team-modal {
    --kbola-canary: ${GlobalStyles.colors.canary};
    --kbola-woodsmoke: ${GlobalStyles.colors.woodsmoke};
  }

  #add-org-modal .kbola-file-native,
  #edit-org-modal .kbola-file-native,
  #add-team-modal .kbola-file-native{
    position:absolute;
    width:1px;height:1px;
    padding:0;margin:-1px;
    overflow:hidden;
    clip: rect(0,0,0,0);
    white-space:nowrap;
    border:0;
  }

  #add-org-modal .kbola-file-shell,
  #edit-org-modal .kbola-file-shell,
  #add-team-modal .kbola-file-shell{
    display:flex;
    align-items:center;
    gap:10px;
    border-radius: 14px;
    border: 1.5px dashed rgba(20,21,26,0.20);
    background: rgba(255,255,255,0.85);
    padding: 10px 10px;
    transition: border-color .12s ease, box-shadow .12s ease, transform .12s ease;
  }

  #add-org-modal .kbola-file-shell:hover,
  #edit-org-modal .kbola-file-shell:hover,
  #add-team-modal .kbola-file-shell:hover{
    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.85);
    box-shadow: 0 12px 26px rgba(0,0,0,0.06);
    transform: translateY(-1px);
  }

  #add-org-modal .kbola-file-shell:focus-within,
  #edit-org-modal .kbola-file-shell:focus-within,
  #add-team-modal .kbola-file-shell:focus-within{
    border-color: var(--kbola-canary);
    box-shadow: 0 0 0 3px rgba(var(--kbs-primary-rgb, 0,173,255), 0.26);
  }

  #add-org-modal .kbola-file-btn,
  #edit-org-modal .kbola-file-btn,
  #add-team-modal .kbola-file-btn{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    gap:8px;
    padding: 10px 12px;
    border-radius: 12px;
    background: var(--kbola-woodsmoke);
    color: var(--kbola-canary);
    font-weight: 900;
    cursor:pointer;
    margin:0;
    user-select:none;
    white-space:nowrap;
    border: 1px solid rgba(20,21,26,0.15);
  }

  #add-org-modal .kbola-file-meta,
  #edit-org-modal .kbola-file-meta,
  #add-team-modal .kbola-file-meta{
    flex:1;
    min-width:0;
  }

  #add-org-modal .kbola-file-name,
  #edit-org-modal .kbola-file-name,
  #add-team-modal .kbola-file-name{
    display:inline-block;
    max-width: 100%;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 800;
    color: var(--kbola-woodsmoke);
    background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
    border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
  }

  #add-org-modal .kbola-file-placeholder,
  #edit-org-modal .kbola-file-placeholder,
  #add-team-modal .kbola-file-placeholder{
    color: rgba(20,21,26,0.45);
    font-weight: 700;
    font-size: 12px;
  }

  #add-org-modal .kbola-file-subhint,
  #edit-org-modal .kbola-file-subhint,
  #add-team-modal .kbola-file-subhint{
    margin-top: 4px;
    font-size: 11px;
    color: rgba(20,21,26,0.48);
    font-weight: 700;
  }

  #add-org-modal .kbola-file-clear,
  #edit-org-modal .kbola-file-clear,
  #add-team-modal .kbola-file-clear{
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1px solid rgba(20,21,26,0.10);
    background: rgba(255,255,255,0.9);
    color: var(--kbola-woodsmoke);
    display:flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
  }

  #add-org-modal .kbola-file-preview,
  #edit-org-modal .kbola-file-preview,
  #add-team-modal .kbola-file-preview{
    display:flex;
    align-items:center;
    gap:12px;
    margin-top: 10px;
  }

  #add-org-modal .kbola-file-thumb,
  #edit-org-modal .kbola-file-thumb,
  #add-team-modal .kbola-file-thumb{
    width: 72px;
    height: 72px;
    border-radius: 14px;
    overflow:hidden;
    border: 1px solid rgba(20,21,26,0.10);
    box-shadow: 0 12px 24px rgba(0,0,0,0.06);
    background:#fff;
  }

  #add-org-modal .kbola-file-thumb img,
  #edit-org-modal .kbola-file-thumb img,
  #add-team-modal .kbola-file-thumb img{
    width:100%;
    height:100%;
    object-fit: cover;
    display:block;
  }

  #add-org-modal .kbola-label,
  #edit-org-modal .kbola-label,
  #add-team-modal .kbola-label{
    font-size: 12.5px;
    font-weight: 800;
    color: rgba(20,21,26,0.70);
    margin-bottom: 6px;
    display:block;
  }
`}</style>
        </>
    );
}
